import { useCallback } from 'react';
import { Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { TbIcon } from 'components/Figma/TbIcon';
import { cn } from 'utils/classNamesTailwind';
import './PresetSidebar.css';
import { Button } from 'components/Button';

interface PresetOption {
  value: string;
  label: string;
  from?: Date;
  to?: Date;
  containsInvalidDates?: boolean;
}

interface PresetSidebarProps {
  onChange: (from: Date | undefined, to: Date | undefined, presetValue: string, labelKey: string) => void;
  value?: string;
  options: PresetOption[];
  optionHeader?: string;
  className?: string;
}

export function PresetSidebar({
  onChange,
  value = undefined,
  options,
  optionHeader = 'datepicker.options.default',
  className = undefined,
}: PresetSidebarProps) {
  const handleChange = useCallback(
    (presetValue: string) => {
      const option = options.find(opt => presetValue === opt.value);
      if (!option) {
        return;
      }

      onChange(option.from, option.to, option.value, option.label);
    },
    [onChange, options]
  );

  const disabledCount = options.filter(o => o.containsInvalidDates).length;
  const hasDisabledDates = disabledCount === 1;
  const hasDisabledPresets = disabledCount > 1;

  return (
    <div
      className={cn(
        'flex flex-col pb-3 border-r border-gray-300 min-w-[180px] max-w-[320px] max-h-[500px] overflow-auto',
        className
      )}
    >
      <h3 className="preset-sidebar-header">
        <FormattedMessage id={optionHeader} defaultMessage="TIME FRAME" />
      </h3>

      {options.map(option => (
        <Button
          key={option.value}
          onClick={() => handleChange(option.value)}
          disabled={option.containsInvalidDates}
          priority="ghost"
          className="preset-sidebar-button"
          data-selected={value === option.value}
          data-testid={`date-preset-${option.value}`}
          iconRight={value === option.value ? 'Checkmark' : undefined}
        >
          <FormattedMessage id={option.label} />
        </Button>
      ))}

      {hasDisabledDates && (
        <div className="flex items-start gap-2 px-6 pt-3 text-gray-500">
          <TbIcon name="FileStack" size="md" className="mt-0.5 shrink-0" />
          <Typography variant="body2" fontSize={10}>
            <FormattedMessage id="datepicker.invalid.preset" />
          </Typography>
        </div>
      )}

      {hasDisabledPresets && (
        <div className="flex items-start gap-2 px-6 pt-3 text-gray-500">
          <TbIcon name="FileStack" size="md" className="mt-0.5 shrink-0" />
          <Typography variant="body2" lineHeight="15px">
            <FormattedMessage id="datepicker.invalid.presets" />
          </Typography>
        </div>
      )}
    </div>
  );
}
